import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";

import PageHeader from "../../components/PageHeader";
import { SELECT_STYLES } from "../../components/DateFilters/constants";
import { BrazilDate } from "../../utils/BrazilDate";
import { lojaService } from "../../services/lojaService";
import { fetchHistoricoPuloSemBorda } from "../../services/historicoPuloSemBordaService";
import { useAuth } from "../../context/AuthContext";
import { canVerTodasLojasUser } from "../../lib/screenAccess";

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function formatHorario(iso: string): string {
  return BrazilDate.formatBR(new Date(iso), true);
}

const HistoricoPuloBorda: React.FC = () => {
  const { user } = useAuth();
  const podeTodasLojas = canVerTodasLojasUser(user);
  const [data, setData] = useState(BrazilDate.formatISO(BrazilDate.now()));
  const [lojaId, setLojaId] = useState<number | null>(
    podeTodasLojas ? null : (user?.lojaId ?? null)
  );
  const [page, setPage] = useState(1);
  const [applied, setApplied] = useState<{
    data: string;
    lojaId: number | null;
  } | null>({
    data: BrazilDate.formatISO(BrazilDate.now()),
    lojaId: podeTodasLojas ? null : (user?.lojaId ?? null),
  });

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
    enabled: podeTodasLojas,
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data: result, isFetching } = useQuery({
    queryKey: ["historico-pulo-borda", applied, page],
    enabled: applied != null,
    queryFn: () =>
      fetchHistoricoPuloSemBorda({
        data: applied!.data,
        lojaId: applied!.lojaId,
        page,
        limit: 50,
      }),
  });

  const handleAplicar = () => {
    setPage(1);
    setApplied({ data, lojaId });
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Pulos por falta de borda"
        description="Histórico de pedidos pulados pelo pizzaiolo quando a borda estava em falta."
      />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="pulo-borda-dia"
              className="text-xs font-medium text-neutral-700"
            >
              Dia
            </label>
            <input
              id="pulo-borda-dia"
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          {podeTodasLojas && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-neutral-700">Loja</span>
              <ReactSelect<LojaOpt, false>
                value={lojaSel}
                onChange={(o: SingleValue<LojaOpt>) =>
                  setLojaId(o?.value ?? null)
                }
                options={lojaOptions}
                isClearable
                placeholder="Todas"
                styles={LOJA_SELECT_STYLES}
                className="text-sm"
              />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      <div className="relative">
        {isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!result || result.itens.length === 0 ? (
          <p className="text-sm text-neutral-600">
            Nenhum pulo por falta de borda neste dia.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-neutral-200">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-700">
                      Pedido
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-700">
                      Borda
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-700">
                      Item
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-700">
                      Usuário
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-700">
                      Horário
                    </th>
                    {podeTodasLojas && (
                      <th className="px-3 py-2 text-left font-semibold text-neutral-700">
                        Loja
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 bg-white">
                  {result.itens.map((row) => (
                    <tr key={row.id} className="hover:bg-neutral-50/80">
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-neutral-900">
                        #{row.displayId}
                      </td>
                      <td className="px-3 py-2 text-neutral-800">
                        {row.bordaNome}
                      </td>
                      <td className="max-w-xs px-3 py-2 text-neutral-800">
                        {row.itemLabel}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-neutral-800">
                        {row.usuarioNome}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-neutral-700">
                        {formatHorario(row.horario)}
                      </td>
                      {podeTodasLojas && (
                        <td className="whitespace-nowrap px-3 py-2 text-neutral-700">
                          {row.lojaNome ?? "—"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 text-sm text-neutral-700">
              <span>
                {result.total} registro{result.total === 1 ? "" : "s"} · página{" "}
                {result.page} de {result.totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-neutral-300 px-3 py-1.5 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={page >= result.totalPages || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-neutral-300 px-3 py-1.5 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default HistoricoPuloBorda;
